class UpsellList extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('.upsell-list__button');
  }

  connectedCallback() {
    // Translation strings (with hardcoded fallbacks in JS)
    this.addingText = this.dataset.addingText || 'Adding...';
    this.errorAddToCartText = this.dataset.errorAddToCartText || 'Failed to add items to cart';
    this.errorRetryText = this.dataset.errorRetryText || 'Failed to add items to cart. Please try again.';

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmit.bind(this));
    }
  }

  async onSubmit(event) {
    event.preventDefault();

    if (this.submitButton) {
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.setAttribute('aria-busy', 'true');
      const buttonText = this.submitButton.querySelector('span');
      if (buttonText) {
        this.originalButtonText = buttonText.textContent;
        buttonText.textContent = this.addingText;
      }
    }

    try {
      // Prepare sections to bundle (matching bundle section pattern)
      const sectionsToBundle = ['variant-added', 'cart-drawer'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const formData = new FormData(this.form);

      // Get first variant ID for sections_url
      const variantIds = Array.from(formData.getAll('id[]'));

      // Set sections properly (matching bundle pattern)
      formData.set('sections', sectionsToBundle.join(','));

      if (variantIds.length > 0) {
        formData.set('sections_url', `${Shopify.routes.root}variants/${variantIds[0]}`);
      }

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const responseJson = await response.json();

      if (response.ok) {
        // Fetch updated cart data
        const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
        const cart = await cartResponse.json();

        // Merge sections from response
        if (responseJson.sections) {
          cart.sections = responseJson.sections;
        }

        // Dispatch variant:add event
        this.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson],
              cart: cart,
            },
          })
        );

        // Dispatch cart:change event
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cart,
            },
          })
        );

        // Open cart drawer
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && window.themeVariables?.settings?.cartType !== 'page') {
          cartDrawer.show();
        }
      } else {
        // Handle error
        const errorMessage = responseJson.description || this.errorAddToCartText;

        // Dispatch cart:error event for theme-level error handling
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              error: errorMessage,
              response: responseJson,
            },
          })
        );

        this.showError(errorMessage);
        console.error('[UpsellList] Cart error:', responseJson);
      }
    } catch (error) {
      console.error('[UpsellList] Network error:', error);

      // Dispatch cart:error event
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: {
            error: error.message,
            originalError: error,
          },
        })
      );

      this.showError(this.errorRetryText);
    } finally {
      if (this.submitButton) {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-busy');
        const buttonText = this.submitButton.querySelector('span');
        if (buttonText && this.originalButtonText) {
          buttonText.textContent = this.originalButtonText;
        }
      }
    }
  }

  showError(message) {
    // Try to find or create an error display element
    let errorDisplay = this.querySelector('[data-error-display]');

    if (!errorDisplay) {
      errorDisplay = document.createElement('div');
      errorDisplay.setAttribute('data-error-display', '');
      errorDisplay.setAttribute('role', 'alert');
      errorDisplay.style.cssText =
        'color: #d00; padding: 10px; margin: 10px 0; border: 1px solid #d00; border-radius: 4px;';
      this.insertBefore(errorDisplay, this.firstChild);
    }

    errorDisplay.textContent = message;
    errorDisplay.style.display = 'block';

    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (errorDisplay) {
        errorDisplay.style.display = 'none';
      }
    }, 5000);
  }
}

if (!window.customElements.get('upsell-list')) {
  window.customElements.define('upsell-list', UpsellList);
}
